import { FocusEvent, useRef } from 'react'
import { storiesHe } from '../../../locales/stories.he'
import { IStory } from '../../../types/stories'
import { StoryStatus } from './storiesState'
import {
  Bubble,
  Cover,
  Label,
  NewBadge,
  QUIET_FOCUS,
  RailRow,
  Ring,
  RingGap,
} from './StoriesRail.style'
import { storyLabel } from './storiesData'
import { useDragScroll } from './useDragScroll'

/**
 * The focus goes to the bubble with no ring, until a key is pressed on it or
 * the focus moves on: Safari (and every iOS browser) draws :focus-visible on
 * any focus() that follows a tap, since a tap there never focuses a button
 */
export const focusQuietly = (button: HTMLElement) => {
  const loud = () => {
    button.removeAttribute(QUIET_FOCUS)
    button.removeEventListener('keydown', loud)
    button.removeEventListener('blur', loud)
  }
  button.setAttribute(QUIET_FOCUS, '')
  button.addEventListener('keydown', loud)
  button.addEventListener('blur', loud)
  button.focus({ preventScroll: true })
  // a bubble that could not take the focus keeps its ring for the keyboard
  if (document.activeElement !== button) {
    loud()
  }
}

/** Tab leaves a bubble the row shows only in part where it is (Chrome) */
const showFocused = (e: FocusEvent<HTMLButtonElement>) => {
  let keyboard = false
  try {
    keyboard = e.currentTarget.matches(`:focus-visible:not([${QUIET_FOCUS}])`)
  } catch {
    // a browser without :focus-visible keeps its own behaviour
  }
  if (keyboard) {
    e.currentTarget.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }
}

interface IStoriesRailProps {
  /** in the order to draw them, right to left */
  stories: IStory[]
  statusOf: (story: IStory) => StoryStatus
  onOpen: (story: IStory) => void
}

export const StoriesRail = ({
  stories,
  statusOf,
  onOpen,
}: IStoriesRailProps) => {
  const rowRef = useRef<HTMLUListElement>(null)
  const { dragging, handlers } = useDragScroll(rowRef)

  return (
    <RailRow ref={rowRef} $dragging={dragging} {...handlers}>
      {stories.map(story => {
        const status = statusOf(story)
        const state =
          status === 'watched'
            ? storiesHe.watched
            : status === 'new'
            ? storiesHe.newBadge
            : ''
        return (
          <li key={story.id} data-story={story.id}>
            <Bubble
              type='button'
              aria-label={[storyLabel(story), state].filter(Boolean).join(', ')}
              onClick={() => onOpen(story)}
              onFocus={showFocused}
            >
              <Ring $status={status}>
                <RingGap $status={status}>
                  <picture>
                    <source
                      media='(min-width: 1024px)'
                      srcSet={story.cover.d}
                    />
                    <Cover
                      src={story.cover.m}
                      alt=''
                      loading='lazy'
                      decoding='async'
                      draggable={false}
                    />
                  </picture>
                </RingGap>
              </Ring>
              {status === 'new' && (
                <NewBadge aria-hidden='true'>{storiesHe.newBadge}</NewBadge>
              )}
              <Label aria-hidden='true' dir='auto'>
                {/* the space after "פרק N:" is part of the bold run */}
                {story.boldTitle && <b>{`${story.boldTitle} `}</b>}
                <span>{story.storyTitle}</span>
              </Label>
            </Bubble>
          </li>
        )
      })}
    </RailRow>
  )
}
